import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import useSWR, { SWRConfiguration } from 'swr';
import { normalizeLocale } from './localePrefs';
import { AbortApiError, ApiError, callMethod } from './api';

/**
 * Drop-in replacement for the mytools `useMethod` hook. It keeps the same
 * signature and SWR return shape (`{ data, error, mutate, isLoading, ... }`)
 * that the Ops Command UI expects, but fetches over the REST API instead of
 * DDP. The SWR key is `[method, ...args, marker]`; the fetcher receives that
 * array. The marker carries the UI language, so a language switch refetches
 * every payload the server words (titles, labels, narratives) in the new
 * language instead of keeping the cached one.
 *
 * Each hook instance aborts the previous in-flight fetch when the key changes
 * or the component unmounts, so rapid filter/scope changes do not stack work
 * on the backend. Client 4xx errors are not retried (avoids OOM-amplifying
 * retry storms on bad requests).
 */
type Args = readonly unknown[];
type MethodKey = [string, ...unknown[]];

export type UseMethodConfig<T> = SWRConfiguration<T, Error> & {
  /**
   * Extra SWR key segment that is NOT sent to the API. Use when the wire args
   * are stable but the response must change (e.g. current-selection membership).
   */
  keyExtra?: unknown;
  /**
   * Growth's demo group: sent as the request's `demo` field and kept in the
   * SWR key, so demo and real responses never share a cache entry.
   */
  demo?: string | null;
};

type KeyMarker = { __locale: string; __keyExtra?: unknown; __demo?: string };

function keyMarker(
  locale: string,
  keyExtra: unknown,
  demo: string | null | undefined,
): KeyMarker {
  return {
    __locale: locale,
    ...(keyExtra !== undefined ? { __keyExtra: keyExtra } : {}),
    ...(demo ? { __demo: demo } : {}),
  };
}

function isKeyMarker(value: unknown): value is KeyMarker {
  return Boolean(
    value && typeof value === 'object' && '__locale' in (value as object),
  );
}

/** True for superseded / Strict-Mode aborted `/api/call` fetches — not real failures. */
export function isMethodAbortError(err: unknown): boolean {
  if (err instanceof AbortApiError) return true;
  if (!(err instanceof Error)) return false;
  return (
    err.name === 'AbortError' ||
    err.name === 'AbortApiError' ||
    err.message === 'Aborted' ||
    err.message === 'Request aborted'
  );
}

/** Payload belongs to the current SWR key — not keepPreviousData from a prior key. */
export function isCurrentSWRData(state: {
  data: unknown;
  error: unknown;
  isValidating: boolean;
}): boolean {
  return !state.isValidating && state.data != null && state.error == null;
}

/** Current key finished with a real error and no payload (first-load fail). */
export function isFailedSWREmpty(state: {
  data: unknown;
  error: unknown;
  isValidating: boolean;
}): boolean {
  return (
    !state.isValidating &&
    state.data == null &&
    state.error != null &&
    !isMethodAbortError(state.error)
  );
}

function shouldRetryOnError(err: unknown): boolean {
  if (isMethodAbortError(err)) return false;
  if (
    err instanceof ApiError &&
    typeof err.status === 'number' &&
    err.status >= 400 &&
    err.status < 500
  ) {
    return false;
  }
  return true;
}

function keyFingerprint(key: MethodKey): string {
  try {
    return JSON.stringify(key);
  } catch {
    return key.join('\0');
  }
}

export function useMethod<T>(
  method: string,
  args: Args | null,
  config?: UseMethodConfig<T>,
) {
  const { keyExtra, demo, ...swrConfig } = config ?? {};
  const abortRef = useRef<AbortController | null>(null);
  const lastDataByKeyRef = useRef<Map<string, T>>(new Map());
  const { i18n } = useTranslation();
  const marker = keyMarker(normalizeLocale(i18n.language), keyExtra, demo);
  const key: MethodKey | null = args ? [method, ...args, marker] : null;
  const keyActive = key != null;

  // Abort only when the key clears (args → null). Do not abort on unmount —
  // React Strict Mode remounts would cancel the first paint's fetch and cache
  // a fake "Aborted" error until the second request finishes.
  // Key changes abort inside the fetcher (new controller replaces the previous).
  useEffect(() => {
    if (!keyActive) {
      abortRef.current?.abort();
      abortRef.current = null;
    }
  }, [keyActive]);

  return useSWR<T, Error, MethodKey | null>(
    key,
    async (swrKey) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      const fp = keyFingerprint(swrKey);
      const [m, ...rest] = swrKey;
      // Strip the trailing marker: locale and keyExtra are cache-only (the
      // locale travels in clientConfig and Accept-Language), demo goes in the body.
      const last = rest[rest.length - 1];
      const a = isKeyMarker(last) ? rest.slice(0, -1) : rest;
      const demoKey = isKeyMarker(last) ? last.__demo : undefined;
      try {
        const data = demoKey
          ? await callMethod<T>(m, a, controller.signal, demoKey)
          : await callMethod<T>(m, a, controller.signal);
        lastDataByKeyRef.current.set(fp, data);
        return data;
      } catch (err) {
        if (err instanceof AbortApiError) {
          // Only reuse data previously fetched for THIS key — never poison a
          // new scope key with another filter's payload.
          const prev = lastDataByKeyRef.current.get(fp);
          if (prev !== undefined) {
            return prev;
          }
          throw err;
        }
        throw err;
      }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      shouldRetryOnError,
      ...swrConfig,
    },
  );
}
